import type {
  APIComponentAttributes,
  APIReferenceAttributes,
  FilterInterfaceFilter,
  FormattingFilter,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { FilterColorData, FilterColorEntry } from '@ardoq/scope-data';
import {
  isComponentIncludedInContextByFilter,
  isReferenceIncludedInContextByFilter,
} from './isIncludedInContextByFilter';

type FormattingOrInterfaceFilter = FormattingFilter | FilterInterfaceFilter;

const deriveComponentFilterColorEntry = (
  component: APIComponentAttributes,
  filters: FormattingOrInterfaceFilter[],
  contextComponent: APIComponentAttributes | undefined,
  scopeData: EnhancedScopeData
): FilterColorEntry | undefined => {
  for (const filter of filters) {
    const { color } = isComponentIncludedInContextByFilter(
      component,
      [filter],
      contextComponent,
      scopeData
    );
    if (color) {
      return { color, version: filter.version ?? 0 };
    }
  }
  return undefined;
};

const deriveReferenceFilterColorEntry = (
  reference: APIReferenceAttributes,
  filters: FormattingOrInterfaceFilter[],
  scopeData: EnhancedScopeData
): FilterColorEntry | undefined => {
  for (const filter of filters) {
    const { color } = isReferenceIncludedInContextByFilter(
      reference,
      [filter],
      scopeData
    );
    if (color) {
      return { color, version: filter.version ?? 0 };
    }
  }
  return undefined;
};

export const deriveFilterColorData = (
  scopeData: EnhancedScopeData,
  formattingFilters: FormattingFilter[],
  focalComponent: APIComponentAttributes | undefined
): FilterColorData => {
  if (formattingFilters.length === 0) return { entities: {} };

  const entities: Record<string, FilterColorEntry> = {};
  for (const component of scopeData.components) {
    const entry = deriveComponentFilterColorEntry(
      component,
      formattingFilters,
      focalComponent,
      scopeData
    );
    if (entry) {
      entities[component._id] = entry;
    }
  }
  for (const reference of scopeData.references) {
    const entry = deriveReferenceFilterColorEntry(
      reference,
      formattingFilters,
      scopeData
    );
    if (entry) {
      entities[reference._id] = entry;
    }
  }
  return { entities };
};
